import {LocationListener, UnregisterCallback} from "history";
import * as React from "react";
import {Route, RouteComponentProps, Switch} from "react-router";
import {DeviceDetection, DeviceSupportedStatuses} from "./constants/DeviceDetection";
import {FlowStates} from "./constants/FlowStates";
import {Logger} from "./constants/Logger";
import {Measurement, MeasurementEvents} from "./constants/Measurement";
import {PubSubEvents, SetFlowStateData} from "./constants/PubSubEvents";
import {RouteComponents} from "./constants/RouteComponents";
import {RouteProps, Routes} from "./constants/Routing";
import {ManualUpload} from "./manual-upload/ManualUpload";
import {CameraProblem} from "./screens/CameraProblem";
import {Finished} from "./screens/Finished";
import {Landing} from "./screens/Landing";
import {LeaveEmbeddedSafari} from "./screens/LeaveEmbeddedSafari";
import {LoggedOut} from "./screens/LoggedOut";
import {Marbles} from "./screens/Marbles";
import {MustGrantPermission} from "./screens/MustGrantPermission";
import {PathNotFound} from "./screens/PathNotFound";
import {SwitchToSafari} from "./screens/SwitchToSafari";
import {Upload} from "./screens/Upload";
import {UuidNotFound} from "./screens/UuidNotFound";
import {Veriff} from "./screens/Veriff";
import {GlobalStyles, H4, NotificationBanner, Paragraph, ThemeProvider} from "scuid-x";
import {AnonApp} from "./AnonApp";
import {SessionApp} from "./SessionApp";
import Alert from "scuid-x/Icons/alert";

const ScamAlertMessage = () => {
    return (
        <>
            <H4>Stay safe from scams.</H4>
            <Paragraph>
                SoFi uses this identity verification as part of your Personal Loan application.
                Do not share this link with anyone. If you haven't applied for a Personal Loan,
                do not complete this step.
            </Paragraph>
        </>
    );
};

export interface AppState {
    deviceSupportedStatus: DeviceSupportedStatuses;
    flowState: FlowStates;
}

class App extends React.Component<any, AppState> {
    private historyChangeUnlistener: UnregisterCallback;

    constructor(props) {
        super(props);
        PubSubEvents.subscribeSetFlowState(this.setFlowStateListener);

        this.state = {
            deviceSupportedStatus: null,
            flowState: FlowStates.BUSINESS_AS_USUAL,
        };
    }

    public render() {
        // Screens that are shown regardless of current route
        switch (this.state.flowState) {
            case FlowStates.LOGGED_OUT:
                return <LoggedOut/>;
            case FlowStates.MARBLES:
                return <Marbles/>;
            case FlowStates.MUST_GRANT_PERMISSION:
                return <MustGrantPermission/>;
            case FlowStates.UUID_NOT_FOUND:
                return <UuidNotFound/>;
            default:
                break;
        }

        return (
            <ThemeProvider>
                <>
                    <GlobalStyles/>
                    <Switch>
                        <Route
                            exact={true}
                            path={Routes.UI_PATHS.FINISHED}
                            component={Finished}
                        />
                        <Route
                            path={Routes.HOME_WITH_LINK}
                            render={(match: RouteComponentProps<any>) => {
                                return <AnonApp
                                    handleRender={this.renderComponentWithId}
                                    uuid={match.match.params.uuid}
                                />;
                            }}
                        />
                        <Route
                            path={Routes.HOME_WITH_SESSION}
                            render={(match: RouteComponentProps<any>) => {
                                return <SessionApp
                                    handleRender={this.renderComponentWithId}
                                    appId={match.match.params.appId}
                                />;
                            }}
                        />
                        <Route
                            component={PathNotFound}
                        />
                    </Switch>
                </>
            </ThemeProvider>
        );
    }

    private renderComponentWithId =
        (screen: RouteComponents, routeProps: RouteComponentProps<RouteProps>) => {
        const appId = routeProps.match.params.appId;
        const uuid = routeProps.match.params.uuid;
        const documentType = routeProps.match.params.documentType;
        const coBorrower = (screen===RouteComponents.UPLOAD_MANUAL_COBORROWER).toString();

        Measurement.prepare(appId, uuid);

        // These should always be shown unless the user is already on a manual upload page, or is finished.
        if (screen === RouteComponents.VERIFF) {
            // Device checks performed as soon as any page is loaded
            switch (this.state.deviceSupportedStatus) {
                case DeviceSupportedStatuses.GET_USER_MEDIA_MISSING:
                    return <CameraProblem appId={appId} possiblyEmbedded={false} uuid={uuid} docType={documentType}/>;
                case DeviceSupportedStatuses.GET_USER_MEDIA_MISSING_POSSIBLY_EMBEDDED:
                    return <CameraProblem appId={appId} possiblyEmbedded={true} uuid={uuid} docType={documentType}/>;
                case DeviceSupportedStatuses.IPHONE_EMBEDDED_SAFARI:
                    return <LeaveEmbeddedSafari/>;
                case DeviceSupportedStatuses.IPHONE_BUT_NOT_SAFARI:
                    return <SwitchToSafari appId={appId} uuid={uuid} docType={documentType}/>;
                case DeviceSupportedStatuses.ANDROID_APPLICATION_FORCE_MANUAL:
                case DeviceSupportedStatuses.IOS_APPLICATION_FORCE_MANUAL:
                    // tslint:disable-next-line:max-line-length
                    return <ManualUpload appId={appId} coBorrower={coBorrower} uuid={uuid} docType={documentType} attemptedVeriff={false}/>;
                default:
                    break;
            }

            // We hit a camera problem even though their device was good
            if (this.state.flowState === FlowStates.CAMERA_PROBLEM) {
                return <CameraProblem appId={appId} possiblyEmbedded={true} uuid={uuid} docType={documentType}/>;
            }
        }

        switch (screen) {
            case RouteComponents.LANDING:
                return <Landing appId={appId} uuid={uuid} coBorrower={coBorrower} docType={documentType}/>;
            case RouteComponents.UPLOAD:
                return <Upload appId={appId} coBorrower={coBorrower} debug={false} uuid={uuid}/>;
            case RouteComponents.UPLOAD_DEBUG:
                return <Upload appId={appId} coBorrower={coBorrower} debug={true} uuid={uuid}/>;
            case RouteComponents.UPLOAD_MANUAL:
            case RouteComponents.UPLOAD_MANUAL_COBORROWER:
                return (
                    <ManualUpload
                        appId={appId}
                        coBorrower={coBorrower}
                        uuid={uuid}
                        docType={documentType}
                        attemptedVeriff={false}
                    />);
            case RouteComponents.ESCAPE_HATCH:
                return (
                    <ManualUpload
                        appId={appId}
                        coBorrower={coBorrower}
                        uuid={uuid}
                        docType={documentType}
                        attemptedVeriff={true}
                    />
                );
            case RouteComponents.VERIFF:
                return (
                    <>
                        <NotificationBanner
                            icon={Alert}
                            isOneTime={true}
                            notificationText={<ScamAlertMessage />}
                        />
                        <Veriff appId={appId} uuid={uuid} coBorrower={coBorrower} docType={documentType}/>
                        <div
                            id="veriff-iframe-container"
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                height: '100%',
                                position: 'relative',
                            }}
                        />
                    </>);
            default:
                return <PathNotFound/>;
        }
    };

    public componentDidMount() {
        DeviceDetection.getDeviceSupportedStatus(navigator)
        .then((deviceStatus) => {
            this.setState({deviceSupportedStatus:deviceStatus});
        });

        this.historyChangeUnlistener = Routes.BROWSER_HISTORY.listen(this.historyChangeListener);
        switch (this.state.deviceSupportedStatus) {
            case DeviceSupportedStatuses.GET_USER_MEDIA_MISSING:
                Measurement.enqueueEvent(MeasurementEvents.DOC_CAP_USER_MEDIA_MISSING);
                break;
            case DeviceSupportedStatuses.GET_USER_MEDIA_MISSING_POSSIBLY_EMBEDDED:
                Measurement.enqueueEvent(MeasurementEvents.DOC_CAP_USER_MEDIA_MISSING_POSS_EMBED);
                break;
            case DeviceSupportedStatuses.IPHONE_BUT_NOT_SAFARI:
                Measurement.enqueueEvent(MeasurementEvents.DOC_CAP_SWITCH_TO_SAFARI);
                break;
            case DeviceSupportedStatuses.IPHONE_EMBEDDED_SAFARI:
                Measurement.enqueueEvent(MeasurementEvents.DOC_CAP_LEAVE_EMBEDDED);
                break;
            case DeviceSupportedStatuses.ANDROID_APPLICATION_FORCE_MANUAL:
                Measurement.enqueueEvent(MeasurementEvents.DOC_CAP_ANDROID_FORCE_MANUAL);
                break;
            case DeviceSupportedStatuses.IOS_APPLICATION_FORCE_MANUAL:
                Measurement.enqueueEvent(MeasurementEvents.DOC_CAP_IOS_FORCE_MANUAL);
                break;
            default:
                break;
        }
    }

    public componentWillUnmount() {
        PubSub.unsubscribe(this.setFlowStateListener);
        this.historyChangeUnlistener();
    }

    public componentWillUpdate(nextProps: any, nextState: AppState) {
        if (this.state.flowState !== nextState.flowState) {
            let measureEvent: MeasurementEvents | null;
            switch (nextState.flowState) {
                case FlowStates.BUSINESS_AS_USUAL:
                    measureEvent = MeasurementEvents.DOC_CAP_BAU;
                    break;
                case FlowStates.CAMERA_PROBLEM:
                    measureEvent = MeasurementEvents.DOC_CAP_CAM_PROB;
                    break;
                case FlowStates.LOGGED_OUT:
                    measureEvent = MeasurementEvents.DOC_CAP_LOG_OUT;
                    break;
                case FlowStates.MARBLES:
                    measureEvent = MeasurementEvents.DOC_CAP_MARB;
                    break;
                case FlowStates.MUST_GRANT_PERMISSION:
                    measureEvent = MeasurementEvents.DOC_CAP_CAM_ACC;
                    break;
                case FlowStates.UUID_NOT_FOUND:
                    measureEvent = MeasurementEvents.DOC_CAP_BAD_UUID;
                    break;
                default:
                    measureEvent = null;
                    break;
            }
            if (measureEvent !== null) {
                Measurement.fireEvent(measureEvent);
            }
        }
    }

    public componentDidCatch(error: any, info: any) {
        Logger.error(`An error was caught by app.tsx handler. Error: ${error}, Info: ${info}`, error, info);
        this.setState({
            flowState: FlowStates.MARBLES,
        });
    }

    private setFlowStateListener = (msg: any, data: SetFlowStateData) => {
        this.setState({
            flowState: data.flowState,
        });
    };

    private historyChangeListener: LocationListener = () => {
        // we want to reset the flow state to BAU any time route changes
        if (this.state.flowState !== FlowStates.BUSINESS_AS_USUAL) {
            this.setState({
                flowState: FlowStates.BUSINESS_AS_USUAL,
            });
        }
    }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.tsx